<script setup>
import { initHotkeySystem } from '@swordhealth/ui'
import { ref, computed, onBeforeMount, watch } from 'vue'
import MemberDrawer from '@/components/drawers/MemberDrawer/index.vue'
import LoadingScreen from '@/components/feedbacks/LoadingScreen.vue'
import NotesModal from '@/components/modals/member-notes/NotesModal.vue'
import RecommendationReasons from '@/components/recommendations/RecommendationReasons.vue'
import SwordDesk from '@/components/sword-desk/SwordDesk.vue'
import ThemeProvider from '@/components/theme/ThemeProvider.vue'
import TimeoutManager from '@/components/TimeoutManager.vue'

import useExercisesAssets from '@/composables/useExercisesAssets'
import useUser from '@/composables/useUser'
import * as config from '@/scripts/app-configs/config'
import { VERSION_FILE } from '@/scripts/app-configs/constants'
import { RECOMMENDATION_STATUS } from '@/scripts/configs/recommendations'
import { events, triggers, utils } from '@/scripts/global-modals-commands'
import ACLModule from '@/scripts/integrations/acl-module'
import store from '@/store'
import ChatPopup from '@/views/chat/components/ChatPopup.vue'
import OnboardingProfileMissingModal from '@/components/modals/OnboardingProfileMissingModal.vue'
import { segment } from '@/utils/segment'
import { EVENT_KEYS } from '@/utils/segment/segment-events'

const chatPopupRoutesRegex = new RegExp(/^\/(patients-funnel)(\/.*)?$/)
const appReady = computed(() => store.getters['system/uiReady'])
const uiLoadingMessage = computed(() => store.getters['system/uiLoadingMessage'])
const isAuthenticated = computed(() => store.getters['user/authentication/isAuthenticated'])

const isDemo = config.get('DEMO_MODE') === 'true'
const { isUserSet, fetchUserOnboardingInfo } = useUser()
const exerciseAssetsVersion = config.get('ASSETS_VERSION')

const globalModalsState = ref(utils.modalStateUtil)

events.onGlobalModalTrigger((modalKey, action, payload) => {
  globalModalsState.value[modalKey] = utils.updateModalStateUtil(globalModalsState.value[modalKey], action, payload)
})

useExercisesAssets({ assetsVersion: exerciseAssetsVersion, fetchEnabled: isUserSet })

const handleOnboardingProfileModal = async () => {
  const onboardingPromptDismissed = await store.dispatch('user/preferences/getPreference', 'dismissOnboardingProfileWarning')

  if (onboardingPromptDismissed) {
    return
  }
  const onboardingInfo = await fetchUserOnboardingInfo()
  const basicOnboardingProfileFilled = onboardingInfo && onboardingInfo.provides_remote_therapy
  const requiredOnboardingProfileInfoFilled = !!(onboardingInfo && onboardingInfo.specialities)

  if (basicOnboardingProfileFilled && requiredOnboardingProfileInfoFilled) {
    return
  }
  const modalContext = basicOnboardingProfileFilled ? 'incompleteProfile' : 'missingProfile'

  triggers.toggleOnboardingProfileMissing({ modalContext })
}

watch(isUserSet, async (userLoggedIn) => {
  if (!userLoggedIn) return
  store.commit('user/authentication/loadStoredAuthTokens')
  store.commit('system/setUIReadyState', false)

  store.commit('results/resetState', null, { root: true }) // Restart the results information
  await store.dispatch('configs/fetch', 'all', { root: true }) // Getting all feature toggle states
  handleOnboardingProfileModal() // Checking if user onboarding profile still needs to be filled
  store.dispatch('recommendations/fetchRecommendationsCount', [RECOMMENDATION_STATUS.NEW], { root: true }).catch((error) => {
    console.error('Failed to fetch recommendations count:', error)
  })
  store.dispatch('general/fetchNotificationsStats', null, { root: true })

  store.commit('system/setUIReadyState', true)
}, { immediate: true })

onBeforeMount(() => {
  store.commit('system/setAppVersion', { context: 'build', versionObj: VERSION_FILE })

  ACLModule.requestUserAccess().catch(() => {
    store.commit('system/setUIReadyState', true)
    ACLModule.redirectToSignIn()
  })
})

initHotkeySystem({
  onHotkeyTrigger: (shortcut, { groupTrackEvent: group = '' }) => {
    segment.trackEventWithUser(EVENT_KEYS.KEYBOARD_SHORTCUT_TRIGGERED, {
      shortcut,
      group,
    })
  },
})
function onTimeoutShown({ remainingSeconds }) {
  segment.trackEventWithUser(EVENT_KEYS.SESSION_TIMEOUT_MODAL_SHOWN, { remainingSeconds })
}

function onTimeoutSnooze({ remainingSeconds }) {
  // Only emit snooze event if the window is in focus (user is on this tab)
  if (!document.hasFocus()) return

  segment.trackEventWithUser(EVENT_KEYS.SESSION_TIMEOUT_MODAL_SNOOZED, { remainingSeconds })
}

function onTimeoutExpires() {
  segment.trackEventWithUser(EVENT_KEYS.SESSION_TIMEOUT_MODAL_EXPIRED, {})
}
</script>

<template>
  <theme-provider class="main-area">
    <loading-screen v-if="!appReady" :text="uiLoadingMessage" />
    <router-view v-if="appReady" />

    <!-- Global modals -->
    <sword-desk
      :class="{'summary-sidebar-open': globalModalsState.memberSidebar.show }"
      :modal-open="globalModalsState.swordDesk.show"
      :payload="globalModalsState.swordDesk.payload"
    />
    <timeout-manager
      @shown="onTimeoutShown"
      @snooze="onTimeoutSnooze"
      @expires="onTimeoutExpires"
    />
    <notes-modal :modal-open="globalModalsState.notes.show" :payload="globalModalsState.notes.payload" />
    <recommendation-reasons :modal-open="globalModalsState.recommendationsReasons.show" />
    <member-drawer
      :modal-open="globalModalsState.memberSidebar.show"
      :payload="globalModalsState.memberSidebar.payload"
    />
    <onboarding-profile-missing-modal
      :modal-open="globalModalsState.onboardingProfileMissing.show"
      :payload="globalModalsState.onboardingProfileMissing.payload"
    />
    <chat-popup
      v-if="isAuthenticated && !isDemo && chatPopupRoutesRegex.test($route.path)"
      :modal-open="globalModalsState.chatPopup.show"
      :payload="globalModalsState.chatPopup.payload"
      :member-sidebar-open="globalModalsState.memberSidebar.show"
    />
  </theme-provider>
</template>
